import React, { useEffect, useMemo, useState } from 'react';
import { moduleCommonStrings, travelingStrings } from '../../content/hobbyModules';
import { validateTravelingForm } from '../../validation/hobbies';
import ChecklistTemplateTools from '../checklists/ChecklistTemplateTools';
import EntryPhotoPicker from '../../components/EntryPhotoPicker';
import useStepperFlow from '../../hooks/useStepperFlow';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

const emptyTrip = {
  id: null,
  title: '',
  destination: '',
  startDate: '',
  endDate: '',
  status: 'Idea',
  tripType: 'Solo',
  tags: '',
  coverUrl: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
  favorite: false,
  notes: '',
  itinerary: [],
  places: [],
  expenses: [],
  packingItems: [],
};

const emptyDay = { id: null, date: '', title: '', notes: '', items: [] };
const emptyPackingItem = { id: null, title: '', category: '', packed: false };
const stepErrorFields = {
  destination: ['title', 'destination', 'startDate', 'endDate', 'status', 'tripType', 'tags'],
  itinerary: ['itinerary', 'places'],
  checklist: ['packingItems'],
  notes: ['notes', 'coverPhotoUrl', 'coverUrl'],
};

/**
 * Performs the checklist key operation.
 *
 * @param {*} item - item value.
 * @returns {*} The derived operation result.
 */
function checklistKey(item) {
  return String(item.title || '').trim().toLowerCase();
}

/**
 * Retrieves destination error message.
 *
 * @param {*} error - Failure being normalized or reported.
 * @returns {*} The derived operation result.
 */
function getDestinationErrorMessage(error) {
  const message = error?.message || '';
  if (message.includes('PLACE_SEARCH_NOT_CONFIGURED')) return travelingStrings.form.errors.notConfigured;
  if (message.includes('PLACE_SEARCH_RATE_LIMITED')) return travelingStrings.form.errors.rateLimited;
  if (message.includes('PLACE_SEARCH_FORBIDDEN')) return travelingStrings.form.errors.forbidden;
  if (message.includes('PLACE_SEARCH_UNAVAILABLE')) return travelingStrings.form.errors.unavailable;
  return message;
}

/**
 * Performs the city name from destination operation.
 *
 * @param {*} destination - destination value.
 * @returns {*} The derived operation result.
 */
function cityNameFromDestination(destination) {
  return String(destination || '').split(',')[0].trim();
}

/**
 * Performs the suggested trip title operation.
 *
 * @param {*} destination - destination value.
 * @returns {*} The derived operation result.
 */
function suggestedTripTitle(destination) {
  const cityName = cityNameFromDestination(destination);
  return cityName ? travelingStrings.form.selectedTripTitle(cityName) : '';
}

/**
 * Performs the date from input operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function dateFromInput(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Performs the input from date operation.
 *
 * @param {*} date - date value.
 * @returns {*} The derived operation result.
 */
function inputFromDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normalizes trip date.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatTripDate(value) {
  const date = dateFromInput(value);
  if (!date) return value;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Determines whether default day title.
 *
 * @param {string} title - title value.
 * @param {number} index - index numeric value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isDefaultDayTitle(title, index) {
  const normalized = String(title || '').trim();
  return !normalized || /^Day \d+$/i.test(normalized) || normalized === travelingStrings.form.dayTitle(index);
}

/**
 * Performs itinerary with dates.
 *
 * @param {*} form - form value.
 * @returns {*} The derived operation result.
 */
function syncItineraryWithDates(form) {
  const start = dateFromInput(form.startDate);
  const end = dateFromInput(form.endDate);
  if (!start || !end || start > end) return form.itinerary || [];

  const existingByDate = new Map((form.itinerary || []).filter((day) => day.date).map((day) => [day.date, day]));
  const existingByIndex = form.itinerary || [];
  const days = [];
  const cursor = new Date(start);
  let index = 0;

  while (cursor <= end) {
    const date = inputFromDate(cursor);
    const existing = existingByDate.get(date) || existingByIndex[index] || {};
    days.push({
      ...emptyDay,
      ...existing,
      date,
      title: isDefaultDayTitle(existing.title, index) ? travelingStrings.form.dayTitle(index) : existing.title,
      notes: existing.notes || '',
      items: [],
    });
    cursor.setDate(cursor.getDate() + 1);
    index += 1;
  }

  return days;
}

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyTrip };
  return {
    ...emptyTrip,
    ...initialData,
    tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : initialData.tags || '',
    itinerary: (initialData.itinerary || []).map((day, index) => ({
      ...emptyDay,
      ...day,
      title: day.title || travelingStrings.form.dayTitle(index),
      notes: day.notes || '',
      items: [],
    })),
    places: initialData.places || [],
    expenses: initialData.expenses || [],
    packingItems: initialData.packingItems?.length ? initialData.packingItems : [],
  };
}

/** Manages the trip stepper, date-derived itinerary days, checklist, notes, and draft output. */
export default function TravelingForm({
  theme,
  onSave,
  onCancel,
  isSaving,
  initialData,
  onDirtyChange,
  destinationResults = [],
  onSearchDestinations,
  isSearchingDestinations = false,
  destinationSearchError,
  isEditing = false,
}) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [photoDraft, setPhotoDraft] = useState({ file: null, previewUrl: '', remove: false });
  const [errors, setErrors] = useState({});
  const [isDestinationMenuOpen, setIsDestinationMenuOpen] = useState(false);
  const [generatedTitle, setGeneratedTitle] = useState(() => suggestedTripTitle(initialForm.destination));
  const steps = useMemo(() => ['destination', 'itinerary', 'checklist', 'notes'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'destination');

  useEffect(() => {
    setForm(initialForm);
    setPhotoDraft((current) => {
      if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return { file: null, previewUrl: '', remove: false };
    });
    setGeneratedTitle(suggestedTripTitle(initialForm.destination));
    resetStepper('destination');
  }, [initialForm, resetStepper]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm) || Boolean(photoDraft.file) || photoDraft.remove);
  }, [form, initialForm, onDirtyChange, photoDraft.file, photoDraft.remove]);

  useEffect(() => {
    const query = form.destination.trim();
    if (query.length < 2 || !onSearchDestinations) return undefined;

    const timer = window.setTimeout(() => {
      onSearchDestinations(query);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [form.destination, onSearchDestinations]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    const nextGeneratedTitle = field === 'destination' ? suggestedTripTitle(value) : generatedTitle;
    setForm((current) => {
      const next = { ...current, [field]: value };

      if (field === 'destination') {
        if (!current.title.trim() || current.title === generatedTitle) {
          next.title = nextGeneratedTitle;
        }
      }

      if (field === 'startDate') {
        const nextStart = dateFromInput(value);
        const currentEnd = dateFromInput(current.endDate);
        if (nextStart && currentEnd && currentEnd < nextStart) {
          next.endDate = value;
        }
        next.itinerary = syncItineraryWithDates(next);
      }

      if (field === 'endDate') {
        next.itinerary = syncItineraryWithDates(next);
      }

      return next;
    });
    if (field === 'destination') {
      setGeneratedTitle(nextGeneratedTitle);
    }
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Updates list item.
   *
   * @param {*} list - list value.
   * @param {number} index - index numeric value.
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateListItem = (list, index, field, value) => {
    setForm((current) => ({
      ...current,
      [list]: current[list].map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
    setErrors((current) => ({ ...current, [`${list}.${index}.${field}`]: undefined }));
  };

  /**
   * Creates list item.
   *
   * @param {*} list - list value.
   * @param {*} item - item value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const addListItem = (list, item) => {
    setForm((current) => ({ ...current, [list]: [...current[list], { ...item }] }));
  };

  /**
   * Removes list item.
   *
   * @param {*} list - list value.
   * @param {number} index - index numeric value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeListItem = (list, index) => {
    setForm((current) => ({ ...current, [list]: current[list].filter((_, itemIndex) => itemIndex !== index) }));
  };

  /**
   * Performs packing template.
   *
   * @param {Array<*>} items - items collection.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const applyPackingTemplate = (items) => {
    setForm((current) => {
      const nonEmptyExistingItems = (current.packingItems || []).filter((item) => checklistKey(item));
      const existingKeys = new Set(nonEmptyExistingItems.map(checklistKey));
      const nextItems = (items || [])
        .filter((item) => item.title && !existingKeys.has(checklistKey(item)))
        .map((item) => ({
          id: `packing_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          title: item.title,
          category: item.category || '',
          packed: false,
        }));

      return { ...current, packingItems: [...nonEmptyExistingItems, ...nextItems] };
    });
  };

  /**
   * Updates day.
   *
   * @param {number} index - index numeric value.
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateDay = (index, field, value) => updateListItem('itinerary', index, field, value);

  /**
   * Performs destination.
   *
   * @param {*} suggestion - suggestion value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const selectDestination = (suggestion) => {
    const nextGeneratedTitle = travelingStrings.form.selectedTripTitle(suggestion.city || cityNameFromDestination(suggestion.destination));
    setForm((current) => ({
      ...current,
      destination: suggestion.destination,
      title: (!current.title.trim() || current.title === generatedTitle) ? nextGeneratedTitle : current.title,
    }));
    setGeneratedTitle(nextGeneratedTitle);
    setIsDestinationMenuOpen(false);
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    const validation = validateTravelingForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'destination', goToSection);
      return;
    }
    await onSave({
      ...form,
      coverPhotoFile: photoDraft.file,
      shouldRemoveCoverPhoto: photoDraft.remove,
    });
  };

  return (
    <form className="recipe-form trip-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Trip editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {travelingStrings.form.sections[section]}
          </button>
        ))}
      </div>

      {activeSection === 'destination' && (
      <section className="recipe-form-section priority-section">
        <h3>{travelingStrings.form.sections.trip}</h3>
        <div className="form-grid three-columns">
          <div className="form-group">
            <label>{travelingStrings.form.fields.destination}</label>
            <div className="destination-picker">
              <input
                value={form.destination}
                onChange={(event) => {
                  updateField('destination', event.target.value);
                  setIsDestinationMenuOpen(true);
                }}
                onFocus={() => setIsDestinationMenuOpen(form.destination.trim().length >= 2)}
                placeholder={travelingStrings.form.placeholders.destination}
                autoComplete="off"
                aria-invalid={Boolean(errors.destination)}
                disabled={isEditing}
                className={isEditing ? 'locked-field-input' : ''}
              />
              {!isEditing && isDestinationMenuOpen && form.destination.trim().length >= 2 && (
                <div className="destination-suggestions">
                  {isSearchingDestinations && <span className="destination-hint">{travelingStrings.form.searchingDestinations}</span>}
                  {!isSearchingDestinations && destinationSearchError && (
                    <span className="destination-hint error-copy">{getDestinationErrorMessage(destinationSearchError)}</span>
                  )}
                  {!isSearchingDestinations && destinationResults.map((suggestion) => (
                    <button type="button" key={suggestion.id} onClick={() => selectDestination(suggestion)}>
                      <span>{suggestion.label}</span>
                      <small>{[suggestion.city, suggestion.country].filter(Boolean).join(', ')}</small>
                    </button>
                  ))}
                  {!isSearchingDestinations && !destinationSearchError && destinationResults.length > 0 && (
                    <span className="destination-powered-by">{travelingStrings.form.poweredByGoogle}</span>
                  )}
                </div>
              )}
            </div>
            {errors.destination && <p className="field-error">{errors.destination}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.title}</label>
            <input value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder={travelingStrings.form.placeholders.title} aria-invalid={Boolean(errors.title)} />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.status}</label>
            <select value={form.status} onChange={(event) => updateField('status', event.target.value)} aria-invalid={Boolean(errors.status)}>
              {travelingStrings.form.statuses.map((status) => <option key={status}>{status}</option>)}
            </select>
            {errors.status && <p className="field-error">{errors.status}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.startDate}</label>
            <input type="date" value={form.startDate} onChange={(event) => updateField('startDate', event.target.value)} aria-invalid={Boolean(errors.startDate)} />
            {errors.startDate && <p className="field-error">{errors.startDate}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.endDate}</label>
            <input type="date" value={form.endDate} min={form.startDate || undefined} onChange={(event) => updateField('endDate', event.target.value)} aria-invalid={Boolean(errors.endDate)} />
            {errors.endDate && <p className="field-error">{errors.endDate}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.tripType}</label>
            <select value={form.tripType} onChange={(event) => updateField('tripType', event.target.value)} aria-invalid={Boolean(errors.tripType)}>
              {travelingStrings.form.tripTypes.map((tripType) => <option key={tripType}>{tripType}</option>)}
            </select>
            {errors.tripType && <p className="field-error">{errors.tripType}</p>}
          </div>

          <div className="form-group">
            <label>{travelingStrings.form.fields.tags}</label>
            <input value={form.tags} onChange={(event) => updateField('tags', event.target.value)} placeholder={travelingStrings.form.placeholders.tags} />
          </div>

          <div className="form-group form-grid-full">
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? travelingStrings.form.favorite : travelingStrings.form.favoriteAction}</span>
            </button>
          </div>
        </div>
      </section>
      )}

      {activeSection === 'itinerary' && (
      <section className="recipe-form-section">
        <div className="section-heading compact-heading">
          <div>
            <p className="eyebrow">{travelingStrings.form.sections.dailyPlan}</p>
            <h3>{travelingStrings.form.sections.itinerary}</h3>
          </div>
        </div>
        <div className="trip-builder-list">
          {form.itinerary.map((day, dayIndex) => (
            <div className="trip-builder-card" key={`day-${dayIndex}`}>
              <div className="trip-day-heading">
                <div>
                  <strong>{travelingStrings.form.dayTitle(dayIndex)}</strong>
                  {formatTripDate(day.date) && <span>{formatTripDate(day.date)}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>{travelingStrings.form.placeholders.dayTitle}</label>
                <input value={day.title} onChange={(event) => updateDay(dayIndex, 'title', event.target.value)} placeholder={travelingStrings.form.dayTitle(dayIndex)} />
              </div>
              <div className="form-group">
                <label>{travelingStrings.form.fields.notes}</label>
                <textarea rows="2" value={day.notes || ''} onChange={(event) => updateDay(dayIndex, 'notes', event.target.value)} placeholder={travelingStrings.form.placeholders.dayNotes} />
              </div>
            </div>
          ))}
          {form.itinerary.length === 0 && (
            <p className="detail-notes">{travelingStrings.form.empty.itineraryNeedsDates}</p>
          )}
        </div>
      </section>
      )}

      {activeSection === 'checklist' && (
      <section className="recipe-form-section">
        <div className="section-heading compact-heading">
          <div>
            <h3 className="accent-section-title">{travelingStrings.form.sections.checklist}</h3>
          </div>
          <div className="checklist-header-actions">
            <ChecklistTemplateTools
              hobbyId="traveling"
              hobbyName="Travel"
              currentItems={form.packingItems}
              defaultName={`${form.title || 'Travel'} checklist`}
              onApplyItems={applyPackingTemplate}
              buildTemplateItems={(items) => items.map((item) => ({ title: item.title, category: item.category || '' }))}
            />
            <button type="button" className="btn-secondary" onClick={() => addListItem('packingItems', emptyPackingItem)}>{travelingStrings.form.actions.addChecklistItem}</button>
          </div>
        </div>
        <div className="trip-builder-list">
          {form.packingItems.map((item, index) => (
            <div className="trip-builder-card compact-row checklist-item-row" key={`pack-${index}`}>
              <div className="form-group">
                <label>{travelingStrings.form.placeholders.packingItem}</label>
                <input value={item.title} onChange={(event) => updateListItem('packingItems', index, 'title', event.target.value)} placeholder={travelingStrings.form.placeholders.packingItem} />
              </div>
              <label className="inline-checkbox checklist-packed-toggle">
                <input type="checkbox" checked={item.packed} onChange={(event) => updateListItem('packingItems', index, 'packed', event.target.checked)} />
                <span>{travelingStrings.form.labels.packed}</span>
              </label>
              <button
                type="button"
                className="row-remove-btn icon-only"
                aria-label={moduleCommonStrings.actions.remove}
                title={moduleCommonStrings.actions.remove}
                onClick={() => removeListItem('packingItems', index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </section>
      )}

      {activeSection === 'notes' && (
      <section className="recipe-form-section">
        <div className="section-heading compact-heading">
          <div>
            <h3 className="accent-section-title">{travelingStrings.form.sections.notes}</h3>
          </div>
        </div>
        <EntryPhotoPicker
          photoUrl={form.coverPhotoUrl}
          draft={photoDraft}
          onChange={setPhotoDraft}
        />
        <div className="form-group">
          <label>{travelingStrings.form.fields.notes}</label>
          <textarea rows="4" value={form.notes} onChange={(event) => updateField('notes', event.target.value)} />
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || !isStepSettled}>
              {isSaving ? moduleCommonStrings.actions.saving : travelingStrings.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
